import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { Trophy, ChevronRight, Timer, Loader2, CheckCircle2, XCircle, Sparkles, Wand2, RefreshCw, History, Trash2, Repeat2, X } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { generatePracticeQuiz, type PracticeQuiz } from "@/lib/practice-quiz.functions";
import { analyzeAssessment, type AdaptiveReport } from "@/lib/adaptive-engine.functions";
import { AdaptiveReportView } from "@/components/adaptive/adaptive-report";
import { toast } from "sonner";

type PracticeAttempt = {
  id: string;
  title: string; subject: string; level: string; topic: string;
  total: number; score: number; percent: number;
  duration_sec: number | null;
  quiz: PracticeQuiz;
  answers: Record<string, number>;
  created_at: string;
};

export const Route = createFileRoute("/learner/quizzes")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Quizzes — MAKAI" }] }),
  component: QuizzesPage,
});

type QuizRow = {
  id: string; title: string; description: string | null;
  time_limit_min: number | null; total_marks: number; pass_mark: number | null;
  difficulty: "easy" | "medium" | "hard"; max_attempts: number | null;
  available_from: string | null; available_to: string | null;
  subject: { name: string } | null;
};

type AttemptRow = { quiz_id: string; percent: number | null; passed: boolean | null; submitted_at: string | null };

const DIFF_TINT: Record<string, string> = {
  easy: "bg-primary/15 text-primary",
  medium: "bg-gold/20 text-gold-foreground",
  hard: "bg-clay/20 text-clay-foreground",
};

const UG_LEVELS = ["P1","P2","P3","P4","P5","P6","P7","S1","S2","S3","S4","S5","S6"];
const UG_SUBJECTS = [
  "Mathematics","English","Science","Social Studies","Religious Education",
  "Biology","Chemistry","Physics","Geography","History","Literature","Agriculture","ICT","Entrepreneurship","Luganda",
];

function QuizzesPage() {
  const { ctx, loading } = useLearnerContext();
  const gen = useServerFn(generatePracticeQuiz);
  const [quizzes, setQuizzes] = useState<QuizRow[]>([]);
  const [attempts, setAttempts] = useState<AttemptRow[]>([]);
  const [practiceAttempts, setPracticeAttempts] = useState<PracticeAttempt[]>([]);
  const [busy, setBusy] = useState(true);
  const [retry, setRetry] = useState<{ quiz: PracticeQuiz; meta: { subject: string; level: string; topic: string; count: number } } | null>(null);
  const [retryLoading, setRetryLoading] = useState<string | null>(null);

  const loadPractice = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from("practice_quiz_attempts")
      .select("id,title,subject,level,topic,total,score,percent,duration_sec,quiz,answers,created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20);
    setPracticeAttempts((data ?? []) as unknown as PracticeAttempt[]);
  }, []);

  useEffect(() => {
    if (!ctx) return;
    (async () => {
      if (ctx.schoolId) {
        const { data: qs } = await supabase
          .from("quizzes")
          .select("id,title,description,time_limit_min,total_marks,pass_mark,difficulty,max_attempts,available_from,available_to,subject:subjects_catalog(name)")
          .eq("school_id", ctx.schoolId).eq("published", true)
          .order("created_at", { ascending: false });
        const { data: at } = await supabase
          .from("quiz_attempts")
          .select("quiz_id,percent,passed,submitted_at")
          .eq("learner_id", ctx.learnerId)
          .not("submitted_at", "is", null);
        setQuizzes((qs ?? []) as never);
        setAttempts((at ?? []) as never);
      }
      await loadPractice();
      setBusy(false);
    })();
  }, [ctx, loadPractice]);

  async function onDeletePractice(id: string) {
    const { error } = await supabase.from("practice_quiz_attempts").delete().eq("id", id);
    if (error) { toast.error("Could not delete attempt"); return; }
    setPracticeAttempts((prev) => prev.filter((a) => a.id !== id));
    toast.success("Attempt deleted");
  }

  async function onRetry(a: PracticeAttempt) {
    setRetryLoading(a.id);
    try {
      const meta = { subject: a.subject, level: a.level, topic: a.topic, count: a.total };
      const fresh = await gen({ data: meta });
      setRetry({ quiz: fresh, meta });
      toast.success("Fresh quiz ready — same topic, new questions");
      setTimeout(() => {
        document.getElementById("retry-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (e) {
      toast.error((e as Error).message || "Could not regenerate quiz");
    } finally {
      setRetryLoading(null);
    }
  }

  const hasSchool = !!ctx?.schoolId;

  if (loading || busy) return <AppShell role="learner" title="Quizzes"><div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppShell>;

  return (
    <AppShell role="learner" title="Quizzes" subtitle="Practice and assessments with instant feedback.">
      {!hasSchool && <PracticeStudio defaultLevel={ctx?.gradeLevel ?? ""} onSaved={loadPractice} />}

      {hasSchool && (quizzes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No quizzes published yet. Check back soon!
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {quizzes.map((q) => {
            const mine = attempts.filter((a) => a.quiz_id === q.id);
            const best = mine.length ? Math.max(...mine.map((a) => Number(a.percent ?? 0))) : null;
            const passedOnce = mine.some((a) => a.passed);
            const used = mine.length;
            const remaining = q.max_attempts ? Math.max(0, q.max_attempts - used) : null;
            const now = Date.now();
            const notYet = q.available_from && new Date(q.available_from).getTime() > now;
            const closed = q.available_to && new Date(q.available_to).getTime() < now;
            const locked = !!(notYet || closed) || remaining === 0;
            return (
              <div key={q.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {q.subject?.name && <span className="rounded-md bg-sky/15 px-2 py-0.5 text-[11px] font-semibold text-sky-foreground">{q.subject.name}</span>}
                      <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold capitalize ${DIFF_TINT[q.difficulty]}`}>{q.difficulty}</span>
                      {passedOnce && <span className="rounded-md bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">Passed</span>}
                    </div>
                    <h3 className="mt-2 font-display text-base font-bold leading-tight">{q.title}</h3>
                    {q.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{q.description}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3 text-gold" />{best != null ? `Best ${best}%` : "Not attempted"}</span>
                      {q.time_limit_min && <span className="inline-flex items-center gap-1"><Timer className="h-3 w-3" />{q.time_limit_min} min</span>}
                      <span>{q.total_marks} marks</span>
                      {q.pass_mark != null && <span>Pass {q.pass_mark}%</span>}
                      {q.max_attempts && <span>{remaining}/{q.max_attempts} left</span>}
                    </div>
                  </div>
                  {locked ? (
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground text-[10px] font-bold">
                      {notYet ? "Soon" : closed ? "Closed" : "Done"}
                    </span>
                  ) : (
                    <Link to="/learner/quizzes/$quizId" params={{ quizId: q.id }} aria-label="Start quiz"
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  )}
                </div>
                {mine.length > 0 && (
                  <details className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-xs">
                    <summary className="cursor-pointer font-semibold">Attempts ({mine.length})</summary>
                    <ul className="mt-2 space-y-1">
                      {mine.slice(0, 5).map((a, i) => (
                        <li key={i} className="flex items-center justify-between">
                          <span className="text-muted-foreground">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}</span>
                          <span className="inline-flex items-center gap-1 font-semibold">
                            {a.passed ? <CheckCircle2 className="h-3 w-3 text-primary" /> : <XCircle className="h-3 w-3 text-clay" />}
                            {a.percent}%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {hasSchool && (
        <div className="mt-6">
          <PracticeStudio defaultLevel={ctx?.gradeLevel ?? ""} compact onSaved={loadPractice} />
        </div>
      )}

      {retry && (
        <section id="retry-panel" className="mt-6 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card p-5 shadow-soft">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold text-primary">
                <Repeat2 className="h-3.5 w-3.5" /> Retry • Fresh questions
              </div>
              <h2 className="mt-2 font-display text-lg font-bold">{retry.quiz.title}</h2>
              <p className="text-xs text-muted-foreground">{retry.meta.subject} • {retry.meta.level} • {retry.meta.topic} • {retry.meta.count} questions</p>
            </div>
            <button onClick={() => setRetry(null)} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-muted">
              <X className="h-3.5 w-3.5" /> Close
            </button>
          </div>
          <PracticePlayer key={retry.quiz.title + retry.meta.topic + practiceAttempts.length} quiz={retry.quiz} onSaved={loadPractice} />
        </section>
      )}

      <PracticeHistory attempts={practiceAttempts} onDelete={onDeletePractice} onRetry={onRetry} retryLoading={retryLoading} />
    </AppShell>
  );
}

function PracticeHistory({ attempts, onDelete, onRetry, retryLoading }: { attempts: PracticeAttempt[]; onDelete: (id: string) => void; onRetry: (a: PracticeAttempt) => void; retryLoading: string | null }) {
  if (attempts.length === 0) {
    return (
      <section className="mt-6 rounded-2xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
        <History className="mx-auto mb-2 h-5 w-5" />
        Your past practice scores will appear here once you submit a quiz.
      </section>
    );
  }
  return (
    <section className="mt-6">
      <div className="mb-3 flex items-center gap-2">
        <History className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-display text-base font-bold">Past practice attempts</h2>
        <span className="text-xs text-muted-foreground">({attempts.length})</span>
      </div>
      <div className="grid gap-3">
        {attempts.map((a) => (
          <details key={a.id} className="rounded-xl border border-border bg-card p-4 shadow-soft">
            <summary className="flex cursor-pointer items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {a.subject} • {a.level} • {a.topic} • {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold ${a.percent >= 50 ? "bg-primary/15 text-primary" : "bg-clay/15 text-clay-foreground"}`}>
                  <Trophy className="h-3 w-3" /> {a.score}/{a.total} ({a.percent}%)
                </span>
                <button
                  onClick={(e) => { e.preventDefault(); onRetry(a); }}
                  disabled={retryLoading === a.id}
                  className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10 disabled:opacity-60"
                  aria-label="Try this quiz again">
                  {retryLoading === a.id
                    ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating…</>
                    : <><Repeat2 className="h-3 w-3" /> Try again</>}
                </button>
                <button onClick={(e) => { e.preventDefault(); onDelete(a.id); }}
                  className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted" aria-label="Delete attempt">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </summary>
            <ol className="mt-4 space-y-3">
              {a.quiz.questions.map((q, idx) => {
                const picked = a.answers[q.id];
                const correct = q.answerIndex;
                return (
                  <li key={q.id} className="rounded-lg border border-border p-3">
                    <div className="text-sm font-semibold">{idx + 1}. {q.prompt}</div>
                    <div className="mt-2 grid gap-1">
                      {q.choices.map((c, i) => {
                        const isPicked = picked === i;
                        const isRight = i === correct;
                        const isWrongPick = isPicked && i !== correct;
                        return (
                          <div key={i} className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                            isRight ? "border-primary bg-primary/10" :
                            isWrongPick ? "border-clay bg-clay/10" :
                            "border-border"
                          }`}>
                            <span className="font-semibold">{String.fromCharCode(65 + i)}.</span>
                            <span className="flex-1">{c}</span>
                            {isRight && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                            {isWrongPick && <XCircle className="h-3.5 w-3.5 text-clay" />}
                          </div>
                        );
                      })}
                    </div>
                    {q.explanation && (
                      <p className="mt-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                        <span className="font-semibold text-foreground">Why: </span>{q.explanation}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          </details>
        ))}
      </div>
    </section>
  );
}


function PracticeStudio({ defaultLevel, compact = false, onSaved }: { defaultLevel?: string; compact?: boolean; onSaved?: () => void | Promise<void> }) {
  const gen = useServerFn(generatePracticeQuiz);
  const [subject, setSubject] = useState("Mathematics");
  const [level, setLevel] = useState(() => {
    const u = (defaultLevel ?? "").toUpperCase();
    return UG_LEVELS.includes(u) ? u : "P5";
  });
  const [topic, setTopic] = useState("Fractions");
  const [count, setCount] = useState(8);
  const [loading, setLoading] = useState(false);
  const [quiz, setQuiz] = useState<PracticeQuiz | null>(null);

  async function onGenerate() {
    if (!topic.trim()) { toast.error("Please enter a topic"); return; }
    setLoading(true);
    setQuiz(null);
    try {
      const res = await gen({ data: { subject, level, topic: topic.trim(), count } });
      setQuiz(res);
    } catch (e) {
      toast.error((e as Error).message || "Failed to generate quiz");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className={`rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-5 shadow-soft ${compact ? "" : ""}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> MAKAI AI Practice • Uganda NCDC
          </div>
          <h2 className="mt-2 font-display text-lg font-bold">Generate a practice quiz</h2>
          <p className="text-xs text-muted-foreground">Pick your class, subject and topic. MAKAI AI builds a fresh quiz aligned to the Ugandan curriculum.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Subject">
          <select value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
            {UG_SUBJECTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Class">
          <select value={level} onChange={(e) => setLevel(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
            {UG_LEVELS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </Field>
        <Field label="Topic">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Fractions, Photosynthesis"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        </Field>
        <Field label="Questions">
          <input type="number" min={3} max={15} value={count} onChange={(e) => setCount(Math.max(3, Math.min(15, Number(e.target.value) || 8)))}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        </Field>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button onClick={onGenerate} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Wand2 className="h-4 w-4" /> Generate quiz</>}
        </button>
        {quiz && (
          <button onClick={onGenerate} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-muted">
            <RefreshCw className="h-3.5 w-3.5" /> New set
          </button>
        )}
      </div>

      {quiz && <PracticePlayer key={quiz.title + Math.random()} quiz={quiz} onSaved={onSaved} />}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function PracticePlayer({ quiz, onSaved }: { quiz: PracticeQuiz; onSaved?: () => void | Promise<void> }) {
  // Per-question instant feedback: picking A/B/C/D locks that question and reveals
  // whether the choice is correct. Learners can then expand "Show working" for a
  // detailed explanation. Attempt is auto-saved once every question is answered.
  const { ctx } = useLearnerContext();
  const analyze = useServerFn(analyzeAssessment);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [explained, setExplained] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [report, setReport] = useState<AdaptiveReport | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const total = quiz.questions.length;
  const answeredCount = Object.keys(answers).length;
  const allDone = answeredCount === total;
  const score = useMemo(
    () => quiz.questions.reduce((s, q) => s + (answers[q.id] === q.answerIndex ? 1 : 0), 0),
    [answers, quiz],
  );
  const pct = total ? Math.round((score / total) * 100) : 0;

  function pick(qId: string, i: number) {
    // Lock the answer once picked — no second guesses on the same question.
    setAnswers((a) => (a[qId] != null ? a : { ...a, [qId]: i }));
  }

  // Auto-save attempt the moment the learner finishes the last question, then
  // run the AI Adaptive Learning Engine for a full performance report.
  useEffect(() => {
    if (!allDone || saved || saving) return;
    (async () => {
      setSaving(true);
      const durationSec = Math.round((Date.now() - startedAt) / 1000);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error } = await supabase.from("practice_quiz_attempts").insert({
            user_id: user.id,
            title: quiz.title,
            subject: quiz.subject,
            level: quiz.level,
            topic: quiz.topic,
            total,
            score,
            percent: pct,
            duration_sec: durationSec,
            quiz: JSON.parse(JSON.stringify(quiz)),
            answers: JSON.parse(JSON.stringify(answers)),
          });
          if (error) throw error;
          await onSaved?.();
        }
        setSaved(true);

        // Fire adaptive analysis (best-effort — failures don't block save)
        if (ctx?.learnerId) {
          setAnalyzing(true);
          const qResults = quiz.questions.map((q) => {
            const picked = answers[q.id];
            const isCorrect = picked === q.answerIndex;
            return {
              prompt: q.prompt,
              correct: picked == null ? null : isCorrect,
              skipped: picked == null,
              topic: quiz.topic,
              learner_answer: picked != null ? (q.choices[picked] ?? "") : "",
              correct_answer: q.choices[q.answerIndex] ?? "",
            };
          });
          try {
            const r = await analyze({
              data: {
                learnerId: ctx.learnerId,
                sourceType: "practice",
                subject: quiz.subject,
                topic: quiz.topic,
                level: quiz.level,
                percent: pct,
                durationSec,
                totalQuestions: total,
                correctCount: score,
                incorrectCount: total - score - qResults.filter((q) => q.skipped).length,
                skippedCount: qResults.filter((q) => q.skipped).length,
                questions: qResults,
              },
            });
            setReport(r);
          } catch (err) {
            // Non-fatal: keep showing the locked answers + simple score below
            console.warn("Adaptive analysis failed", err);
          } finally {
            setAnalyzing(false);
          }
        }
      } catch (e) {
        toast.error("Could not save attempt: " + ((e as Error).message ?? "unknown"));
      } finally {
        setSaving(false);
      }
    })();
  }, [allDone, saved, saving, answers, score, pct, total, quiz, startedAt, onSaved, ctx, analyze]);

  return (
    <div className="mt-5 space-y-4">
      {(analyzing || report) && (
        <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-4">
          {analyzing && !report && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              MAKAI AI is analysing your performance…
            </div>
          )}
          {report && <AdaptiveReportView report={report} />}
        </div>
      )}

    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display text-base font-bold">{quiz.title}</h3>
        <div className="text-[11px] text-muted-foreground">{quiz.level} • {quiz.subject} • {total} questions</div>
      </div>


      <ol className="mt-4 space-y-4">
        {quiz.questions.map((q, idx) => {
          const picked = answers[q.id];
          const locked = picked != null;
          const correct = q.answerIndex;
          const isCorrect = locked && picked === correct;
          const isExplained = !!explained[q.id];
          return (
            <li key={q.id} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold">{idx + 1}. {q.prompt}</div>
                {locked && (
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    isCorrect ? "bg-primary/10 text-primary" : "bg-clay/10 text-clay"
                  }`}>
                    {isCorrect ? "Correct" : "Incorrect"}
                  </span>
                )}
              </div>
              <div className="mt-2 grid gap-1.5">
                {q.choices.map((c, i) => {
                  const isPicked = picked === i;
                  const showRight = locked && i === correct;
                  const showWrong = locked && isPicked && i !== correct;
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={locked}
                      onClick={() => pick(q.id, i)}
                      className={`flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition ${
                        showRight ? "border-primary bg-primary/10" :
                        showWrong ? "border-clay bg-clay/10" :
                        isPicked ? "border-primary/60 bg-primary/5" :
                        "border-border hover:bg-muted/50 cursor-pointer"
                      }`}>
                      <span className="font-semibold">{String.fromCharCode(65 + i)}.</span>
                      <span className="flex-1">{c}</span>
                      {showRight && <CheckCircle2 className="h-4 w-4 text-primary" />}
                      {showWrong && <XCircle className="h-4 w-4 text-clay" />}
                    </button>
                  );
                })}
              </div>

              {locked && q.explanation && (
                <div className="mt-2">
                  {!isExplained ? (
                    <button
                      type="button"
                      onClick={() => setExplained((e) => ({ ...e, [q.id]: true }))}
                      className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10">
                      <Sparkles className="h-3 w-3" /> Show working & explanation
                    </button>
                  ) : (
                    <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap">
                      <span className="font-semibold text-foreground">Working: </span>{q.explanation}
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
          Score: {score} / {total} ({pct}%){saving ? " • saving…" : allDone ? "" : " • in progress"}
        </div>
        <span className="text-[11px] text-muted-foreground">{answeredCount}/{total} answered</span>
      </div>
    </div>
    </div>
  );
}

